import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  FileText, BarChart3, TrendingUp, CheckCircle2, Clock, AlertTriangle,
  DollarSign, Users, Award, Brain, Eye, ChevronRight, Scale
} from 'lucide-react';
import { proposalAPI } from '../api';
import { Loader, ProgressBar } from '../App.core';
import { formatDate, formatRelative } from '../utils/helpers';

const STATUS_COLORS = {
  draft: '#64748b', review: '#f59e0b', approved: '#059669', issued: '#2563eb', closed: '#6b7280',
  received: '#64748b', under_review: '#f59e0b', evaluated: '#2563eb', shortlisted: '#8b5cf6', accepted: '#059669', rejected: '#ef4444',
};

export const RFPDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    proposalAPI.dashboard()
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return null;

  const { kpis, rfps_by_status, proposals_by_status, recent_proposals, recent_rfps } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <Link to="/rfps" className="rfp-detail-breadcrumb">
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to RFP Management
            </Link>
            <div className="rfp-detail-title-wrap">
              <h1 className="rfp-detail-title">RFP & Procurement Dashboard</h1>
              <span className="rfp-detail-number-badge">OVERVIEW</span>
            </div>
            <div className="rfp-detail-submeta">
              <span className="rfp-detail-submeta-item">
                <BarChart3 size={14} style={{ color: '#0038A7' }} />
                Real-time procurement analytics & tender evaluations
              </span>
              <span className="rfp-detail-submeta-item">
                <FileText size={14} style={{ color: '#64748b' }} />
                {kpis.total_rfps || 0} RFPs active
              </span>
              <span className="rfp-detail-submeta-item">
                <Users size={14} style={{ color: '#64748b' }} />
                {kpis.total_proposals || 0} Proposals received
              </span>
            </div>
          </div>

          <div className="rfp-detail-actions">
            <Link to="/rfps" className="btn btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FileText size={15} /> All RFPs
            </Link>
            <Link to="/proposals" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Eye size={15} /> All Proposals
            </Link>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 0 }}>
        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Total RFPs</span>
            <div className="kpi-card-icon-pill">
              <FileText size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.total_rfps}</div>
          <div className="kpi-card-footer">
            <span>Packages in system</span>
          </div>
        </div>

        <div className="kpi-card kpi-cyan">
          <div className="kpi-card-header">
            <span className="kpi-label">Proposals Received</span>
            <div className="kpi-card-icon-pill">
              <Users size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.total_proposals}</div>
          <div className="kpi-card-footer">
            <span>Submitted tender bids</span>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-card-header">
            <span className="kpi-label">Pending Review</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.proposals_pending}</div>
          <div className="kpi-card-footer">
            <span>Awaiting technical review</span>
          </div>
        </div>

        <div className="kpi-card" style={{ borderColor: 'rgba(139, 92, 246, 0.3)' }}>
          <div className="kpi-card-header">
            <span className="kpi-label">Evaluated</span>
            <div className="kpi-card-icon-pill" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
              <Brain size={16} />
            </div>
          </div>
          <div className="kpi-value" style={{ color: '#8b5cf6' }}>{kpis.proposals_evaluated}</div>
          <div className="kpi-card-footer">
            <span>Propel AI analyzed</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Accepted</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.proposals_accepted}</div>
          <div className="kpi-card-footer">
            <span>Awarded proposals</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Avg Overall Score</span>
            <div className="kpi-card-icon-pill" style={{ background: 'rgba(100, 116, 139, 0.1)', color: '#475569' }}>
              <Award size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.avg_score}%</div>
          <div style={{ marginTop: 4 }}>
            <ProgressBar value={kpis.avg_score || 0} />
          </div>
        </div>

        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Avg Technical</span>
            <div className="kpi-card-icon-pill">
              <Scale size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.avg_technical}%</div>
          <div style={{ marginTop: 4 }}>
            <ProgressBar value={kpis.avg_technical || 0} />
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-card-header">
            <span className="kpi-label">Avg Commercial</span>
            <div className="kpi-card-icon-pill">
              <DollarSign size={16} />
            </div>
          </div>
          <div className="kpi-value">{kpis.avg_commercial}%</div>
          <div style={{ marginTop: 4 }}>
            <ProgressBar value={kpis.avg_commercial || 0} />
          </div>
        </div>
      </div>

      {/* Two Column Distribution Panels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 }}>
        {/* RFP Pipeline */}
        <div className="card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column' }}>
          <div className="card-header" style={{ marginBottom: 16, borderBottom: '1px solid var(--px-border)', paddingBottom: 12 }}>
            <div>
              <div className="card-title" style={{ fontSize: '1rem', fontWeight: 700 }}>RFP Procurement Pipeline</div>
              <div className="card-subtitle">Distribution of packages across lifecycle states</div>
            </div>
            <Link to="/rfps" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0038A7', textDecoration: 'none' }}>
              View all &rarr;
            </Link>
          </div>
          {(rfps_by_status || []).length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--px-text-muted)', margin: 'auto 0', textAlign: 'center', padding: 24 }}>No RFPs created yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rfps_by_status.map(s => {
                const pct = kpis.total_rfps > 0 ? Math.round((parseInt(s.count) / kpis.total_rfps) * 100) : 0;
                const color = STATUS_COLORS[s.status] || '#64748b';
                return (
                  <div key={s.status} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 110, fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize', color: '#1e293b' }}>
                      {s.status}
                    </div>
                    <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999, transition: 'width 0.4s ease' }} />
                    </div>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, minWidth: 40, textAlign: 'right', color: '#0b1220' }}>
                      {s.count} <span style={{ fontWeight: 400, color: 'var(--px-text-muted)', fontSize: '0.74rem' }}>({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Proposal Status Distribution */}
        <div className="card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column' }}>
          <div className="card-header" style={{ marginBottom: 16, borderBottom: '1px solid var(--px-border)', paddingBottom: 12 }}>
            <div>
              <div className="card-title" style={{ fontSize: '1rem', fontWeight: 700 }}>Proposal Status Distribution</div>
              <div className="card-subtitle">Review workflow breakdown for received vendor bids</div>
            </div>
            <Link to="/proposals" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0038A7', textDecoration: 'none' }}>
              View all &rarr;
            </Link>
          </div>
          {(proposals_by_status || []).length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--px-text-muted)', margin: 'auto 0', textAlign: 'center', padding: 24 }}>No proposals submitted yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {proposals_by_status.map(s => {
                const pct = kpis.total_proposals > 0 ? Math.round((parseInt(s.count) / kpis.total_proposals) * 100) : 0;
                const color = STATUS_COLORS[s.status] || '#64748b';
                return (
                  <div key={s.status} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 110, fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize', color: '#1e293b' }}>
                      {s.status.replace('_', ' ')}
                    </div>
                    <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999, transition: 'width 0.4s ease' }} />
                    </div>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, minWidth: 40, textAlign: 'right', color: '#0b1220' }}>
                      {s.count} <span style={{ fontWeight: 400, color: 'var(--px-text-muted)', fontSize: '0.74rem' }}>({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Active RFPs with proposal counts */}
      <div className="card" style={{ padding: '20px 22px' }}>
        <div className="card-header" style={{ marginBottom: 14, borderBottom: '1px solid var(--px-border)', paddingBottom: 12 }}>
          <div>
            <div className="card-title" style={{ fontSize: '1rem', fontWeight: 700 }}>Active RFPs & Tender Proposals</div>
            <div className="card-subtitle">Procurement packages with proposal counts, evaluation status, and leading scores</div>
          </div>
          <Link to="/rfps" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
            Browse All Packages
          </Link>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>RFP Title & Reference</th>
                <th>Associated Project</th>
                <th>Status</th>
                <th>Proposals</th>
                <th>AI Evaluated</th>
                <th>Leading Score</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(recent_rfps || []).length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--px-text-muted)' }}>
                    No active RFPs found.
                  </td>
                </tr>
              ) : (
                (recent_rfps || []).map(r => {
                  const props = r.proposals || [];
                  const evalCount = props.filter(p => p.overall_score !== null).length;
                  const bestScore = props.reduce((max, p) => Math.max(max, p.overall_score || 0), 0);
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 650, color: '#0b1220' }}>{r.title}</div>
                        <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', fontFamily: 'var(--px-font-mono)', marginTop: 2 }}>
                          {r.rfp_number}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--px-text-secondary)' }}>
                        <strong>{r.project?.name || 'Unassigned'}</strong>
                        {r.project?.project_code && (
                          <span style={{ fontSize: '0.72rem', color: 'var(--px-text-muted)', marginLeft: 6 }}>
                            ({r.project.project_code})
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            background: `${STATUS_COLORS[r.status] || '#64748b'}18`,
                            color: STATUS_COLORS[r.status] || '#64748b',
                            border: `1px solid ${STATUS_COLORS[r.status] || '#64748b'}35`,
                            fontWeight: 650,
                            textTransform: 'uppercase'
                          }}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.88rem', fontWeight: 600 }}>{props.length}</td>
                      <td style={{ fontSize: '0.88rem', fontWeight: 600, color: evalCount > 0 ? '#8b5cf6' : 'var(--px-text-muted)' }}>
                        {evalCount} / {props.length}
                      </td>
                      <td>
                        {bestScore > 0 ? (
                          <span style={{ fontWeight: 700, color: bestScore >= 70 ? 'var(--px-success)' : '#d97706' }}>
                            {bestScore}%
                          </span>
                        ) : (
                          <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => navigate(`/rfps/${r.id}/proposals`)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        >
                          <Eye size={13} /> View Proposals
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Proposals Activity */}
      <div className="card" style={{ padding: '20px 22px' }}>
        <div className="card-header" style={{ marginBottom: 14, borderBottom: '1px solid var(--px-border)', paddingBottom: 12 }}>
          <div>
            <div className="card-title" style={{ fontSize: '1rem', fontWeight: 700 }}>Recent Proposal Activity</div>
            <div className="card-subtitle">Latest vendor submissions and AI evaluation scoring updates</div>
          </div>
          <Link to="/proposals" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
            All Proposals
          </Link>
        </div>
        {(recent_proposals || []).length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--px-text-muted)', textAlign: 'center', padding: 28 }}>No proposals logged in system yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recent_proposals.map(p => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--px-border)',
                  background: '#ffffff',
                  gap: 14,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0038A7'; e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--px-border)'; e.currentTarget.style.background = '#ffffff'; }}
                onClick={() => navigate(`/proposals/${p.id}`)}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: `${STATUS_COLORS[p.status] || '#64748b'}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: STATUS_COLORS[p.status] || '#64748b',
                    flexShrink: 0
                  }}
                >
                  {p.status === 'accepted' ? (
                    <CheckCircle2 size={18} style={{ color: 'var(--px-success)' }} />
                  ) : p.status === 'evaluated' ? (
                    <Brain size={18} style={{ color: '#8b5cf6' }} />
                  ) : (
                    <FileText size={18} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0b1220' }}>
                    {p.vendor_name} {p.vendor_company && <span style={{ color: 'var(--px-text-secondary)', fontWeight: 500 }}>— {p.vendor_company}</span>}
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)', marginTop: 2 }}>
                    <span style={{ fontFamily: 'var(--px-font-mono)', fontWeight: 600 }}>{p.rfp?.rfp_number}</span> &bull; {p.rfp?.title}
                  </div>
                </div>
                <div style={{ width: 130, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  <span
                    className="badge"
                    style={{
                      background: `${STATUS_COLORS[p.status] || '#64748b'}18`,
                      color: STATUS_COLORS[p.status] || '#64748b',
                      border: `1px solid ${STATUS_COLORS[p.status] || '#64748b'}35`,
                      fontWeight: 650,
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      display: 'inline-block',
                      minWidth: 96
                    }}
                  >
                    {p.status}
                  </span>
                </div>
                <div style={{ width: 70, flexShrink: 0, textAlign: 'right' }}>
                  {p.overall_score !== null ? (
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: p.overall_score >= 70 ? 'var(--px-success)' : '#d97706' }}>
                      {p.overall_score}%
                    </span>
                  ) : (
                    <span style={{ color: 'var(--px-text-muted)', fontSize: '0.85rem' }}>—</span>
                  )}
                </div>
                <div style={{ width: 140, flexShrink: 0, textAlign: 'right' }}>
                  {p.total_price ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--px-text-secondary)' }}>
                      {Number(p.total_price).toLocaleString()} {p.currency || 'AED'}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--px-text-muted)', fontSize: '0.8rem' }}>—</span>
                  )}
                </div>
                <div style={{ width: 100, flexShrink: 0, textAlign: 'right' }}>
                  <span style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)' }}>
                    {formatRelative(p.created_at)}
                  </span>
                </div>
                <ChevronRight size={16} style={{ color: 'var(--px-text-muted)', flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
